import {computed, effect, inject, Injectable, signal} from '@angular/core';
import Keycloak, { KeycloakProfile } from 'keycloak-js';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, from, Observable, of, shareReplay, switchMap } from 'rxjs';
import {UserApiService} from '../../features/admin/services/user-api.service';
import {UserDetail} from '../../features/admin/services/user-api.models';
import {hasPermission, PERMISSIONS} from './permissions';

@Injectable({
  providedIn: 'root',
})
export class Auth {
  private api = inject(UserApiService);
  private keycloak = inject(Keycloak);

  private readonly profileCache = new Map<string, Observable<UserDetail>>();

  readonly profile = toSignal(from(this.keycloak.loadUserProfile()));

  private readonly userId = computed(() => this.profile()?.id);

  readonly fullProfile = toSignal(
    toObservable(this.userId).pipe(
      distinctUntilChanged(),
      switchMap((id) => {
        if (!id || !hasPermission(this.keycloak, PERMISSIONS.usersView)) {
          return of(null);
        }
        return this.getUserCached(id);
      }),
    ),
    { initialValue: null },
  );

  readonly profileTitle = computed<string>(() => this.fullProfile()?.title || '');

  readonly fullName = computed(() => {
    const p = this.profile();
    if (!p) return '';
    return [p.firstName, p.lastName].filter(Boolean).join(' ');
  });

  readonly initials = computed(() => {
    const p = this.profile();
    if (!p) return '';
    return [p.firstName, p.lastName]
      .filter(Boolean)
      .map((n) => n![0].toUpperCase())
      .join('');
  });

  private getUserCached(id: string): Observable<UserDetail> {
    let cached$ = this.profileCache.get(id);
    if (!cached$) {
      cached$ = this.api.getUser(id).pipe(shareReplay({ bufferSize: 1, refCount: false }));
      this.profileCache.set(id, cached$);
    }
    return cached$;
  }
}
